import { useContext, useEffect, useRef, useMemo, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import classNames from "classnames";
import { scrollTo } from "seamless-scroll-polyfill";

import {
  useAuthContext,
  PlatformContext,
  useWindowDimensions,
} from "@ascendnow/shared";

import AscLoading from "../AscLoading/AscLoading";
import AscSidebar from "../AscSidebar/AscSidebar";
import AscTopNavigation from "../AscTopNavigation/AscTopNavigation";
import { AscPlatformLayoutProps } from "./AscPlatformLayout.types";
import {
  AscPlatformLayoutContext,
  AscPlatformLayoutProvider,
} from "./AscPlatformLayoutContext/AscPlatformLayoutContext";
import { useResponsive } from "../../lib";
import styles from "./AscPlatformLayout.module.scss";
import { ReactComponent as LogoSvg } from "../../assets/illustrations/logo.svg";
import { ReactComponent as CollapsedLogoSvg } from "../../assets/illustrations/collapsedLogo.svg";
import ProfilePicture from "../../assets/images/sample-avatar-1.png";

const MAX_CONTENT_WIDTH = 1920;

function AscPlatformLayout(props: AscPlatformLayoutProps) {
  const { pathname } = useRouter();
  const { isMedium } = useResponsive();
  const { width: windowWidth } = useWindowDimensions();
  const {
    user,
    logout,
    loading: authLoading,
    isAuthenticated,
  } = useAuthContext();
  const {
    collapseSidebar,
    setCollapseSidebar,
    activePathsSideBarItemId,
    props: propsStack,
  } = useContext(AscPlatformLayoutContext);
  const {
    coinsCount,
    notificationsCount,
    globalRank,
    schoolRank: classRank,
    points,
    loading: userDataLoading,
  } = useContext(PlatformContext);

  const mainContentStyle = {
    "--max-content-width": `${MAX_CONTENT_WIDTH}px`,
  } as React.CSSProperties;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  const {
    className,
    children,
    title,
    subTitle,
    showBackButton,
    sidebarItems,
    userDropdownOptions,
    hideSidebarAndNavigation = false,
    globalRankUrl,
    classRankUrl,
    loading = false,
    onBackButtonClick,
  } = useMemo(() => {
    if (propsStack?.sm && windowWidth <= 480) {
      return {
        ...props,
        ...propsStack,
        ...propsStack?.md,
        ...propsStack?.sm,
      };
    }
    if (propsStack?.md && windowWidth <= 768) {
      return {
        ...props,
        ...propsStack,
        ...propsStack?.md,
      };
    }
    return {
      ...props,
      ...propsStack,
    };
  }, [props, propsStack, windowWidth]);

  function resize() {
    const width = wrapperRef.current?.offsetWidth || MAX_CONTENT_WIDTH;
    mainContentRef.current?.setAttribute(
      "style",
      `max-width: ${width < MAX_CONTENT_WIDTH ? width : MAX_CONTENT_WIDTH}px`
    );
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      //reset main content width and remove resize listener if sidebar and navigation are hidden or is loading
      if (hideSidebarAndNavigation || loading || authLoading) {
        mainContentRef.current?.setAttribute("style", `max-width: 100%`);
        window.removeEventListener("resize", resize);
        return;
      }

      resize();
      window.addEventListener("resize", resize);
    }

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [hideSidebarAndNavigation, loading, authLoading, collapseSidebar]);

  useEffect(() => {
    if (mainContentRef.current) {
      scrollTo(mainContentRef.current, {
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [pathname]);

  return (
    <AscLoading className={styles.loading} loading={loading || authLoading}>
      <div
        data-testid="asc-platform-layout"
        className={classNames(
          styles.ground,
          {
            [styles.hideSidebar]:
              hideSidebarAndNavigation ||
              authLoading ||
              loading ||
              !isAuthenticated(),
            [styles.hideTopNavigation]:
              hideSidebarAndNavigation ||
              authLoading ||
              loading ||
              !isAuthenticated(),
          },
          className
        )}
      >
        {title && pathname !== "/" ? (
          <Head>
            <title key="title">The Edge: {title}</title>
          </Head>
        ) : null}
        <AscSidebar
          width={isMedium ? "15.25rem" : "20rem"}
          collapsed={collapseSidebar}
          onClose={() => setCollapseSidebar?.(true)}
          header={<LogoSvg className={styles.logo} />}
          collapsedHeader={<CollapsedLogoSvg className={styles.collapsed} />}
          className={styles.sidebar}
          headerClassName={styles.sidebarHeader}
          collapsedHeaderClassName={styles.sidebarCollapsedHeader}
          selectedId={activePathsSideBarItemId}
          onToggleCollapse={() => setCollapseSidebar?.((prev) => !prev)}
          mobileOptions={{
            name: user?.name || "",
            avatarUrl: user?.profilePicture?.location ?? ProfilePicture,
            coinsCount: coinsCount,
            globalRank: globalRank,
            classRank: classRank,
            globalRankUrl: globalRankUrl,
            classRankUrl: classRankUrl,
            points: points,
            onLogout: logout,
            userDropdownOptions: userDropdownOptions?.map(
              ({ name, ...rest }) => ({
                ...rest,
                children: name,
              })
            ),
          }}
        >
          {sidebarItems.map((item) => (
            <AscSidebar.Item
              key={item.id}
              id={item.id}
              icon={item.icon}
              path={item.path}
              disabled={item.disabled}
            >
              {item.name}
            </AscSidebar.Item>
          ))}
        </AscSidebar>
        <div ref={wrapperRef} className={styles.wrapper}>
          <AscTopNavigation
            showLogo={false}
            loading={userDataLoading}
            className={styles.topNavigation}
            title={title}
            subTitle={subTitle}
            showBackButton={showBackButton}
            notificationsCount={notificationsCount}
            name={user?.name || ""}
            avatarUrl={user?.profilePicture?.location ?? ProfilePicture}
            coinsCount={coinsCount}
            globalRank={globalRank}
            classRank={classRank}
            globalRankUrl={globalRankUrl}
            classRankUrl={classRankUrl}
            points={points}
            onLogout={logout}
            onBackButtonClick={onBackButtonClick}
            userDropdownOptions={userDropdownOptions?.map(
              ({ name, ...rest }) => ({
                ...rest,
                children: name,
              })
            )}
          />
          <div
            ref={mainContentRef}
            className={styles.mainContent}
            style={mainContentStyle}
          >
            {children}
          </div>
        </div>
      </div>
    </AscLoading>
  );
}

function WithWrappedInProvider({
  activePath,
  ...props
}: AscPlatformLayoutProps & {
  activePath?: string;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div>Not mounted</div>;
  }

  return (
    <AscPlatformLayoutProvider
      activePath={activePath}
      pathToSidebarIdMap={props?.sidebarItems?.map((item) => ({
        sidebarItemId: item.id,
        path: item.path,
      }))}
    >
      <AscPlatformLayout {...props} />
    </AscPlatformLayoutProvider>
  );
}

export default WithWrappedInProvider;
